import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { Subject, takeUntil } from 'rxjs';
import { GlobalServicesService } from '../services/global-services.service';
import { LoginService } from '../services/login.service';

// Interfaces for type safety
interface SSOCallbackParams {
  code: string;
  state: string;
  nonce?: string;
  provider?: string;
  error?: string;
  error_description?: string;
}

interface OAuthCallbackParams {
  access_token: string;
  id_token: string;
}

interface SSOCallbackResponse {
  access_token: string;
  token_type: string;
  user_info?: any;
  is_new_user?: boolean;
}

interface UserProfileResponse {
  auth_user_permissions: string[];
  company_metadata: {
    company_config: {
      dashboard: boolean;
    };
  };
}

@Component({
    selector: 'app-login-callback',
    templateUrl: './login-callback.component.html',
    styleUrls: ['./login-callback.component.css'],
    standalone: false
})
export class LoginCallbackComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private storage = window.localStorage;
  private sessionStorage = window.sessionStorage;

  constructor(
    private messageService: MessageService,
    private globalService: GlobalServicesService,
    private loginService: LoginService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.handleCallback();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.messageService.clear();
  }

  private handleCallback(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const ssoParams = this.extractSSOParams(urlParams);

    if (this.isSSOCallback(ssoParams)) {
      this.handleSSOCallback(ssoParams);
    } else {
      this.handleOAuthCallback();
    }
  }

  private extractSSOParams(urlParams: URLSearchParams): SSOCallbackParams {
    return {
      code: urlParams.get('code') || '',
      state: urlParams.get('state') || '',
      nonce: urlParams.get('nonce') || undefined,
      provider: urlParams.get('provider') || undefined,
      error: urlParams.get('error') || undefined,
      error_description: urlParams.get('error_description') || undefined,
    };
  }

  private isSSOCallback(params: SSOCallbackParams): boolean {
    return !!(params.code && params.state);
  }

  private handleSSOCallback(params: SSOCallbackParams): void {
    if (params.error) {
      this.handleSSOError(params.error, params.error_description);
      return;
    }

    if (!this.validateState(params.state)) {
      this.handleSecurityError('Invalid state parameter. Possible CSRF attack.');
      return;
    }

    this.processSSOCallback(params);
  }

  private handleSSOError(error: string, errorDescription?: string): void {
    const errorMsg = errorDescription || `SSO Error: ${error}`;
    this.showErrorMessage('SSO Authentication Failed', errorMsg);
    this.cleanupSSOCallback();
  }

  private handleSecurityError(message: string): void {
    this.showErrorMessage('Security Error', message);
    this.cleanupSSOCallback();
  }

  private validateState(state: string): boolean {
    const storedState = this.sessionStorage.getItem('sso_state');
    return state === storedState;
  }

  private processSSOCallback(params: SSOCallbackParams): void {
    const payload = {
      code: params.code,
      state: params.state,
      nonce: params.nonce,
      provider: params.provider,
    };

    this.loginService.ssoCallbackFastApi(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => this.handleSSOSuccess(response),
        error: (error: any) => this.handleSSOFailure(error),
      });
  }

  private handleSSOSuccess(response: any): void {
    const ssoResponse = response.body as SSOCallbackResponse;
    
    if (!ssoResponse?.access_token) {
      this.showErrorMessage('SSO Error', 'Invalid response from SSO provider.');
      return;
    }

    this.storeAuthTokens(ssoResponse);
    this.fetchUserProfileAndRedirect(ssoResponse);
  }

  private storeAuthTokens(ssoResponse: SSOCallbackResponse): void {
    const accessToken = {
      access_token: ssoResponse.access_token,
      token_type: ssoResponse.token_type || 'Bearer',
    };
    
    this.storage.setItem('tokenFastApi', JSON.stringify(accessToken));
    
    if (ssoResponse.user_info) {
      this.storage.setItem('userInfo', JSON.stringify(ssoResponse.user_info));
    }
  }

  private fetchUserProfileAndRedirect(ssoResponse: SSOCallbackResponse): void {
    this.globalService.getUserProfileFastApi()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => this.handleUserProfileSuccess(res, ssoResponse),
        error: (error: any) => this.handleUserProfileError(error),
      });
  }

  private handleUserProfileSuccess(res: any, ssoResponse: SSOCallbackResponse): void {
    const userProfile = res.body as UserProfileResponse;
    
    this.storage.removeItem('CompanyDetails');
    
    const redirectPath = this.determineRedirectPath(userProfile);
    const message = this.getSuccessMessage(ssoResponse);
    
    this.showSuccessMessage('Authentication Success', message);
    this.cleanupSSOCallback();
    this.router.navigateByUrl(redirectPath);
  }

  private determineRedirectPath(userProfile: UserProfileResponse): string {
    const privilege = userProfile.auth_user_permissions || [];
    const dashboardEnabled = userProfile.company_metadata?.company_config?.dashboard ?? false;
    const hasEventAccess = privilege.includes('event.view') || privilege.includes('event.create');
    
    if (dashboardEnabled === true) {
      return '/dashboard';
    } else if (hasEventAccess) {
      return '/events';
    }
    
    return '/settings';
  }

  private getSuccessMessage(ssoResponse: SSOCallbackResponse): string {
    return ssoResponse.is_new_user
      ? 'SSO authentication successful! Welcome to NeoHire.'
      : 'SSO authentication successful!';
  }

  private handleUserProfileError(error: any): void {
    console.error('Error getting user profile:', error);
    this.router.navigate(['/dashboard']);
  }

  private handleSSOFailure(error: any): void {
    const errorMsg = error.error?.detail || 'SSO authentication failed. Please try again.';
    this.showErrorMessage('SSO Error', errorMsg);
    this.cleanupSSOCallback();
    
    setTimeout(() => {
      this.router.navigate(['/login'], { 
        queryParams: { 
          error: 'sso_failed',
          message: errorMsg 
        }
      });
    });
  }

  private handleOAuthCallback(): void {
    const userInfoEndpoint = this.storage.getItem('userinfo-ep');
    const fragment = window.location.hash.substring(1);
    const params = new URLSearchParams(fragment);
    
    const oauthParams: OAuthCallbackParams = {
      access_token: params.get('access_token') || '',
      id_token: params.get('id_token') || '',
    };

    if (this.isValidOAuthCallback(oauthParams, userInfoEndpoint)) {
      this.handleOAuthSuccess(oauthParams, userInfoEndpoint!);
    } else {
      this.showErrorMessage('Login Failed', 'Failed to login via SSO!');
    }
  }

  private isValidOAuthCallback(params: OAuthCallbackParams, userInfoEndpoint: string | null): boolean {
    return !!(params.access_token && params.id_token && userInfoEndpoint);
  }

  private handleOAuthSuccess(params: OAuthCallbackParams, userInfoEndpoint: string): void {
    // Implement OAuth callback handling logic here
    // This method should handle the original OAuth flow
    console.log('OAuth callback handling not implemented');
  }

  private showSuccessMessage(summary: string, detail: string): void {
    this.messageService.add({
      severity: 'success',
      summary,
      detail,
    });
  }

  private showErrorMessage(summary: string, detail: string): void {
    this.messageService.add({
      severity: 'error',
      summary,
      detail,
    });
  }

  private cleanupSSOCallback(): void {
    const url = new URL(window.location.href);
    const paramsToRemove = ['code', 'state', 'nonce', 'error', 'error_description', 'provider'];
    
    paramsToRemove.forEach(param => url.searchParams.delete(param));
    
    window.history.replaceState({}, document.title, url.toString());
    this.sessionStorage.removeItem('sso_state');
    
    // Additional security cleanup
    this.clearSensitiveStorage();
  }

  private clearSensitiveStorage(): void {
    // Clear any temporary SSO-related data
    const ssoKeys = ['sso_nonce', 'sso_provider', 'sso_redirect_uri'];
    ssoKeys.forEach(key => {
      this.sessionStorage.removeItem(key);
      this.storage.removeItem(key);
    });
  }

  private validateOrigin(): boolean {
    // Validate that we're on the expected domain to prevent redirect attacks
    const currentOrigin = window.location.origin;
    const expectedOrigins = [
      window.location.origin, // Current domain
      // Add other allowed origins if needed
    ];
    
    return expectedOrigins.includes(currentOrigin);
  }
}
